import { useMemo } from "react";
import { toggleSort as sharedToggleSort, toNumber } from "@/shared/utils";
import { PURCHASE_STATUS_ORDER } from "@/features/accounting/shared";
import type { SortConfig } from "@/shared/utils";
import type { PurchaseListItem } from "../types";
import { transactionStatusToPurchaseStatus } from "../types/api";

export type SortKey = "invoice_number" | "date" | "vendor" | "amount" | "invoice_category" | "document_type" | "status";

export type { SortConfig };

export function usePurchasesSort(
  purchases: PurchaseListItem[],
  sortConfig: SortConfig<SortKey> | null,
): PurchaseListItem[] {
  return useMemo(() => {
    if (!sortConfig) {
      return purchases;
    }

    const { key, direction } = sortConfig;

    return [...purchases].sort((a, b) => {
      let cmp = 0;

      if (key === "invoice_number") {
        cmp = a.invoice_number.localeCompare(b.invoice_number);
      } else if (key === "date") {
        const aDate = a.invoice_date ? new Date(a.invoice_date).getTime() : 0;
        const bDate = b.invoice_date ? new Date(b.invoice_date).getTime() : 0;
        cmp = aDate - bDate;
      } else if (key === "vendor") {
        cmp = a.vendor_name.localeCompare(b.vendor_name);
      } else if (key === "amount") {
        cmp = toNumber(a.total_amount) - toNumber(b.total_amount);
      } else if (key === "invoice_category") {
        cmp = (a.invoice_category ?? "").localeCompare(b.invoice_category ?? "");
      } else if (key === "document_type") {
        cmp = (a.document_type ?? "").localeCompare(b.document_type ?? "");
      } else if (key === "status") {
        const aStatus = transactionStatusToPurchaseStatus(a.transaction_status);
        const bStatus = transactionStatusToPurchaseStatus(b.transaction_status);
        cmp = (PURCHASE_STATUS_ORDER[aStatus] ?? -1) - (PURCHASE_STATUS_ORDER[bStatus] ?? -1);
      }

      return direction === "asc" ? cmp : -cmp;
    });
  }, [purchases, sortConfig]);
}

export function toggleSort(currentSort: SortConfig<SortKey> | null, newKey: SortKey): SortConfig<SortKey> {
  return sharedToggleSort(currentSort, newKey);
}
